import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { RuntimeOptions } from "./types";
export interface ConversationModelChoice {
  value: string;
  seriesId: string;
  modelName: string;
  description: string;
}
export function ConversationModelSelect({
  options,
  seriesId,
  menuDirection,
  disabled,
  onChange,
  emptyLabel = "无可用系列",
}: {
  options: RuntimeOptions | null;
  seriesId: string | null;
  menuDirection: "up" | "down";
  disabled: boolean;
  onChange: (seriesId: string) => void;
  emptyLabel?: string;
}) {
  const choices = conversationModelChoices(options);
  const value = seriesId ?? "";
  const selectedChoice = choices.find((choice) => choice.value === value);
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLSpanElement>(null);
  const unavailable = disabled || choices.length === 0;

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", closeOnOutsideClick);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOnOutsideClick);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (unavailable) setOpen(false);
  }, [unavailable]);

  return (
    <span className="conversation-model-picker" ref={pickerRef}>
      <button
        aria-label="模型系列"
        aria-expanded={open}
        aria-haspopup="listbox"
        className="conversation-model-trigger"
        disabled={unavailable}
        title={selectedChoice?.description ?? "选择本会话使用的模型系列"}
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selectedChoice?.modelName ?? emptyLabel}</span>
        <ChevronDown
          aria-hidden="true"
          className={`conversation-model-chevron ${open ? "open" : ""}`}
          size={13}
        />
      </button>
      {open && (
        <span
          aria-label="选择模型系列"
          className={`conversation-model-menu opens-${menuDirection}`}
          role="listbox"
        >
          {choices.map((choice) => {
            const selected = choice.value === value;
            return (
              <button
                aria-label={choice.modelName}
                aria-selected={selected}
                className={`conversation-model-option ${selected ? "selected" : ""}`}
                key={choice.value}
                role="option"
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (!selected) onChange(choice.seriesId);
                }}
              >
                <span className="conversation-model-option-label">
                  <strong>{choice.modelName}</strong>
                  <small>{choice.description}</small>
                </span>
                {selected && <Check aria-hidden="true" size={15} />}
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}

export function conversationModelChoices(
  options: RuntimeOptions | null,
): ConversationModelChoice[] {
  return (
    options?.series.map((series) => ({
      value: series.seriesId,
      seriesId: series.seriesId,
      modelName: series.displayName,
      description: `规划：${series.planner.displayName}；处理：${series.worker.displayName}；视觉：${series.vision.map((model) => `${model.providerDisplayName} / ${model.displayName}`).join(" → ") || "未配置"}`,
    })) ?? []
  );
}
export function defaultConversationModelChoice(
  options: RuntimeOptions | null,
  choices: ConversationModelChoice[],
): ConversationModelChoice | undefined {
  return choices.find((choice) => choice.value === options?.default_series_id) ?? choices[0];
}
